import { ApiError, type api } from "./api.ts";
import type {
  CredentialRecoveryStore,
  GuestLinkCredentialRecovery,
} from "./credential-recovery.ts";

/** The service will never answer this receipt again: 409, 410 or 422. */
export function credentialRecoveryCannotContinue(cause: unknown): boolean {
  return cause instanceof ApiError && [409, 410, 422].includes(cause.status);
}

/**
 * Ask the service for a guest link's share URL with the receipt's
 * Idempotency-Key: the first call issues the link, a repeat inside the
 * recovery window returns the same URL. Records the link id on the receipt.
 */
export async function issueGuestLinkFromReceipt(input: {
  readonly csrf: string;
  readonly recovery: GuestLinkCredentialRecovery;
  readonly request: typeof api;
  readonly store: CredentialRecoveryStore;
}): Promise<{
  readonly recovery: GuestLinkCredentialRecovery;
  readonly shareUrl: string;
}> {
  const { recovery } = input;
  const result = await input.request<{
    guest_link: {
      id: string;
      recoverable_until: string;
      share_url: string;
    };
  }>(`/v1/appnotes/sites/${recovery.siteId}/guest-links`, {
    method: "POST",
    csrf: input.csrf,
    idempotencyKey: recovery.operationId,
    body: { expires_at: recovery.expiresAt },
  });
  return {
    recovery: input.store.setIssuedGuestLink(recovery, {
      id: result.guest_link.id,
      recoverableUntil: result.guest_link.recoverable_until,
    }),
    shareUrl: result.guest_link.share_url,
  };
}

export type GuestLinkCopyOutcome =
  /** On the clipboard; the receipt is marked copied and keeps Copy. */
  | {
      readonly kind: "copied";
      readonly recovery: GuestLinkCredentialRecovery;
    }
  /** The clipboard refused; show the URL in the save dialog. */
  | {
      readonly kind: "show";
      readonly recovery: GuestLinkCredentialRecovery;
      readonly shareUrl: string;
    }
  /** The service will not return this URL again; the receipt is gone. */
  | { readonly kind: "dropped"; readonly cause: unknown }
  /** A failure worth retrying; the receipt stays. */
  | { readonly kind: "failed"; readonly cause: unknown };

/**
 * Copy on a guest link row: recover the share URL through its receipt (a POST
 * replay with the receipt's Idempotency-Key) and put it on the clipboard.
 * When the clipboard refuses (no user activation left after the request, or
 * no permission), the caller shows the URL in the save dialog instead.
 */
export async function copyGuestLinkFromReceipt(input: {
  readonly csrf: string;
  readonly recovery: GuestLinkCredentialRecovery;
  readonly request: typeof api;
  readonly store: CredentialRecoveryStore;
  readonly writeClipboard: (text: string) => Promise<void>;
}): Promise<GuestLinkCopyOutcome> {
  let issued: Awaited<ReturnType<typeof issueGuestLinkFromReceipt>>;
  try {
    issued = await issueGuestLinkFromReceipt(input);
  } catch (cause) {
    if (!credentialRecoveryCannotContinue(cause))
      return { kind: "failed", cause };
    input.store.remove(input.recovery.id);
    return { kind: "dropped", cause };
  }
  try {
    await input.writeClipboard(issued.shareUrl);
  } catch {
    return { kind: "show", ...issued };
  }
  return { kind: "copied", recovery: input.store.markCopied(issued.recovery) };
}
